import React, { useEffect, useMemo, useState } from "react"
import PracticeMap from "./PracticeLocationsMap"
import {
  PublicLocation,
  PublicLocationPhoto,
} from "../../../../domain/Profile/schema"
import { fetchLocationPhotoThumbnail } from "../../../../domain/Profile/model"
import { getCombinedAddress, sortLocations } from "./helpers"
import { LocationListIem } from "./LocationListItem"
import { useCMSReady } from "../../../../domain/CMS/useCMSReady"
import { isEmpty } from "lodash"
import { LocationTypeDisplayId } from "../../../../domain/Practice/schema"
import { sortProfilePhotos } from "../../../shared/utils"
import { ButtonWrapper } from "../../../shared/components/ButtonWrapper"
import ColumnContainer from "../../../shared/components/Layout/ColumnContainer"
import {
  Typography,
  typographyTypes,
} from "../../../shared/components/Typography"
import Icon from "../../../shared/components/Icon"

interface LocationProps {
  practiceId: string
  locations?: PublicLocation[]
  selectedMarkerId: string
  setSelectedMarkerId: React.Dispatch<React.SetStateAction<string | undefined>>
}

export type LocationPhoto = {
  id: string
  name?: string
  fileName: string
  imageUrl: string
  description?: string
  isLoading?: boolean
}

type LocationPhotoRecord = Record<string, LocationPhoto>

export const LocationMap: React.FC<LocationProps> = ({
  practiceId,
  locations,
  selectedMarkerId,
  setSelectedMarkerId,
}) => {
  const { cms: cmsContent } = useCMSReady(["publicProfileSlide"])
  const publicProfileText = cmsContent.publicProfileSlide

  const [locationThumbnail, setLocationThumbnail] =
    useState<LocationPhotoRecord>({})
  const [geocodeLocationErrorIds, setGeocodeLocationErrorIds] = useState<
    string[]
  >([])
  const [mapExpanded, setMapExpanded] = useState(true)
  const sortedLocations = sortLocations(locations || [])

  const markersFromPracticeLocations = useMemo(
    () =>
      locations
        ?.filter(
          (location) =>
            location.locationTypeDisplayId !== LocationTypeDisplayId.VIRTUAL,
        )
        .map((location) => ({
          id: location.id as string,
          address: getCombinedAddress(location),
          title: getCombinedAddress(location),
        })) || [],
    [locations],
  )

  useEffect(() => {
    setSelectedMarkerId?.(markersFromPracticeLocations[0]?.id ?? null)
  }, [markersFromPracticeLocations, setSelectedMarkerId])

  useEffect(() => {
    const fetchPhotoThumbnailUrl = () => {
      const getSortedPhotos = (photos: PublicLocationPhoto[]) => {
        return photos.toSorted(sortProfilePhotos)
      }
      if (!locations) return
      locations
        .filter(
          (location) =>
            location.locationTypeDisplayId !== LocationTypeDisplayId.VIRTUAL,
        )
        .filter((location) => !isEmpty(location.locationPhotos))
        .map(async (location) => {
          const defaultThumbnail = getSortedPhotos(location.locationPhotos)[0]
          const encodedLocationPhotoString = await fetchLocationPhotoThumbnail(
            practiceId,
            defaultThumbnail.id,
          )
          if (!encodedLocationPhotoString) return
          const locationPhotoRecordValue: LocationPhoto = {
            id: defaultThumbnail.id,
            name: defaultThumbnail.name,
            fileName: defaultThumbnail.fileName,
            imageUrl: encodedLocationPhotoString,
            description: defaultThumbnail.description,
          }
          setLocationThumbnail((prev) => ({
            ...prev,
            [location.id]: locationPhotoRecordValue,
          }))
        })
    }
    fetchPhotoThumbnailUrl()
  }, [locations, practiceId])

  const hideExpandMapText = mapExpanded
    ? publicProfileText?.locationHideMap
    : publicProfileText?.locationShowMap

  return (
    <ColumnContainer styleOverrides="space-y-0">
      {locations?.length && locations.length > 0 && (
        <ButtonWrapper
          onClick={() => setMapExpanded(!mapExpanded)}
          styleOverrides="p-0 ml-6 mb-2"
          childStyleOverrides="flex flex-row space-x-4"
        >
          <Typography
            variant={typographyTypes.NORMAL}
            text={hideExpandMapText}
            styleOverrides="text-link-blue w-[80px]"
          />
          <Icon
            iconType="chevronUpBlue"
            styleOverrides={mapExpanded ? "" : "rotate-180"}
          />
        </ButtonWrapper>
      )}
      {mapExpanded && locations?.length && locations.length > 0 && (
        <PracticeMap
          markers={markersFromPracticeLocations}
          selectedMarkerId={selectedMarkerId || ""}
          setSelectedMarkerId={setSelectedMarkerId}
          setGeocodeLocationErrorIds={setGeocodeLocationErrorIds}
        />
      )}
      {sortedLocations.map((location) => (
        <LocationListIem
          key={location.id}
          location={location}
          locations={locations || []}
          selectedMarkerId={selectedMarkerId}
          showDivider={sortedLocations.indexOf(location) !== 0}
          onClick={() => setSelectedMarkerId(location.id)}
          thumbnail={
            locationThumbnail[location.id] ?? {
              id: "",
              fileName: "",
              name: "",
              imageUrl: "",
              isLoading: true,
            }
          }
          geocodeLocationErrorIds={geocodeLocationErrorIds}
        />
      ))}
    </ColumnContainer>
  )
}
